import React, {useState, useEffect} from 'react';
import analytics from '@react-native-firebase/analytics';
import {useNavigation, useRoute} from '@react-navigation/native';
import {SafeAreaView} from 'react-native-safe-area-context';
import {useTheme} from 'styled-components/native';

import {Header} from '../../../components/Interface/Headers/Header';
import {Titles} from '../../../components/Interface/Typographies/Titles';
import {Button} from '../../../components/Interface/Forms/Buttons/Button';
import {simularCredito} from '../../../services/creditoMova';
import {EmprestimoEligibilityGate} from '../../../components/shared/EmprestimoEligibilityGate';
import {useGlobal} from '../../../context/global';
import {
  AmountInput,
  AmountError,
  Container,
  Content,
  MaxAmountCard,
  MaxAmountIcon,
  MaxAmountText,
  OptionsLabel,
  PrazoInput,
  PrazoError,
} from './styles';
import { Loading } from '../../../components/Interface/Loadings/Loading';

const MIN_PRAZO = 1;
const MAX_PRAZO = 12;
const MOVA_PRODUCT_ID = 1406;

type RouteParams = {
  prefill_valor_centavos?: number;
  prefill_prazo_meses?: number;
};

export function EmprestimoSimulacao() {
  const navigation = useNavigation();
  const route = useRoute();
  const theme = useTheme();
  const {fetchValorMaximoEmprestimo, valorEmprestimo} = useGlobal();
  const params = (route.params || {}) as RouteParams;

  const [amount, setAmount] = useState('');
  const [amountError, setAmountError] = useState('');
  const [prazo, setPrazo] = useState('');
  const [prazoError, setPrazoError] = useState('');


  const formatReais = (value: number) =>
    value > 0
      ? `R$ ${value.toLocaleString('pt-BR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`
      : '';

  useEffect(() => {
  analytics().logEvent('emprestimo_tela_simulacao');
  fetchValorMaximoEmprestimo();
  if (params.prefill_valor_centavos) {
    setAmount(formatReais(Math.round(params.prefill_valor_centavos / 100)));
  }
  if (params.prefill_prazo_meses) {
    setPrazo(String(params.prefill_prazo_meses));
  }
}, []);

  const [loading, setLoading] = useState(false);

  // O display tem sempre ",00" no final, então os dígitos brutos são [inteiro][00].
  // Comparamos a contagem de dígitos antes e depois para detectar o que mudou.
  const getIntegerFromDisplay = (display: string) => {
    const d = display.replace(/\D/g, '');
    return d.length >= 2 ? d.slice(0, -2) : d;
  };

  const amountValue = (() => {
    const intStr = getIntegerFromDisplay(amount);
    return intStr ? Number(intStr) : 0;
  })();

  const minAmount = 5;
  const maxAmount = valorEmprestimo || 999999;

  const prazoValue = Number(prazo.replace(/\D/g, ''));
  const isAmountValid = amountValue > 0 && amountValue <= maxAmount;
  const isPrazoValid = prazoValue >= MIN_PRAZO && prazoValue <= MAX_PRAZO;
  const isFormValid = isAmountValid && isPrazoValid;

  const handleAmountChange = (text: string) => {
    const newDigits = text.replace(/\D/g, '');
    const prevDigits = amount.replace(/\D/g, '');
    const prevIntStr = getIntegerFromDisplay(amount);
    const delta = newDigits.length - prevDigits.length;

    let newIntStr: string;
    if (delta > 0) {
      // Usuário digitou — encontra posição de inserção
      let insertPos = 0;
      while (insertPos < prevDigits.length && newDigits[insertPos] === prevDigits[insertPos]) {
        insertPos++;
      }
      const newDigit = newDigits[insertPos];
      const intPos = Math.min(insertPos, prevIntStr.length);
      newIntStr = prevIntStr.slice(0, intPos) + newDigit + prevIntStr.slice(intPos);
    } else if (delta < 0) {
      // Backspace — encontra posição removida
      let removePos = 0;
      while (removePos < newDigits.length && newDigits[removePos] === prevDigits[removePos]) {
        removePos++;
      }
      if (removePos < prevIntStr.length) {
        newIntStr = prevIntStr.slice(0, removePos) + prevIntStr.slice(removePos + 1);
      } else {
        newIntStr = prevIntStr.slice(0, -1);
      }
    } else {
      return;
    }

    const value = newIntStr ? Number(newIntStr) : 0;
    if (value > maxAmount) {
      setAmountError(`Valor máximo permitido é R$ ${maxAmount.toLocaleString('pt-BR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`);
      return;
    }
    if (value === 0 || value >= minAmount) {
      setAmountError('');
    }
    setAmount(formatReais(value));
  };

  const handlePrazoChange = (text: string) => {
    const digits = text.replace(/\D/g, '');
    if (!digits) {
      setPrazo('');
      setPrazoError('');
      return;
    }
    const value = Number(digits);
    const clamped = value > MAX_PRAZO ? MAX_PRAZO : value < MIN_PRAZO ? MIN_PRAZO : value;
    setPrazoError('');
    setPrazo(String(clamped));
  };

 const handleContinue = async () => {
  analytics().logEvent('emprestimo_clicou_continuar_simulacao');
  if (amountValue < minAmount) {
    setAmountError(`Valor mínimo permitido é R$ ${minAmount},00`);
    return;
  }
  if (prazoValue < MIN_PRAZO || prazoValue > MAX_PRAZO) {
    setPrazoError(`Informe um prazo entre ${MIN_PRAZO} e ${MAX_PRAZO} parcelas`);
    return;
  }
  try {
    setLoading(true);
    const response = await simularCredito({
        valor_centavos: amountValue * 100,
        prazo_meses: prazoValue,
        mova_product_id: MOVA_PRODUCT_ID,
      });
      (navigation as any).navigate('EmprestimoSimulacaoResultado', {simulacao: response});
    } catch (error: any) {
      const body = error?.response?.data;
      const errorMessage: string | undefined = body?.error || body?.msg || body?.message || undefined;
      navigation.navigate('ConfirmationScreen', {
        title: 'Ocorreu um problema',
        text: errorMessage || 'Ocorreu um erro ao simular seu empréstimo. Por favor, tente novamente mais tarde.',
        buttonAction: () => navigation.goBack(),
        closeIconAction: () => navigation.navigate('Initial'),
        buttonText: 'Tentar novamente',
        type: 'error',
        showSupport: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    {
      loading && <Loading />
    }
    <SafeAreaView edges={[]} style={{flex: 1}}>
      <EmprestimoEligibilityGate />
      <Container>
        <Header icon="chevron-left" />
        <Content
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{paddingBottom: 24}}>
          <Titles
            title="Simular empréstimo"
            subtitle="Tenha dinheiro rápido para organizar suas contas, lidar com imprevistos ou realizar seus planos. Simule, escolha o valor, as parcelas e solicite tudo direto pelo aplicativo, com transparência e segurança."
          />
          {!!valorEmprestimo && (
            <MaxAmountCard>
              <MaxAmountIcon name="alert-circle" />
              <MaxAmountText>
                Valor máximo disponível:{' '}
                <MaxAmountText style={{fontWeight: 'bold'}}>
                  R$ {valorEmprestimo.toLocaleString('pt-BR')},00
                </MaxAmountText>
              </MaxAmountText>
            </MaxAmountCard>
          )}
          <OptionsLabel>Valor do empréstimo:</OptionsLabel>
          <AmountInput
            placeholder="R$ 0,00"
            placeholderTextColor={theme.colors.placeholder}
            keyboardType="numeric"
            value={amount}
            onChangeText={handleAmountChange}
          />
          {!!amountError && <AmountError>{amountError}</AmountError>}
          <OptionsLabel>Parcelas (Até 12):</OptionsLabel>
          <PrazoInput
            placeholder="Quantas parcelas?"
            placeholderTextColor={theme.colors.placeholder}
            keyboardType="numeric"
            maxLength={2}
            value={prazo}
            onChangeText={handlePrazoChange}
          />
          {!!prazoError && <PrazoError>{prazoError}</PrazoError>}
          <Button
            title={ 'Continuar'}
            onPress={handleContinue}
            enabled={isFormValid && !loading}
            color={isFormValid && !loading ? undefined : theme.colors.silver_dark}
          />
        </Content>
      </Container>
    </SafeAreaView>
    </>
  );
}
